import React from 'react';
import { Target, Award, Clock, Activity, Leaf, Info } from 'lucide-react';
import { BarChart, Bar, XAxis, ResponsiveContainer } from 'recharts';

export default function PricingPanel({ project }) {
    const pricing = project.pricing;
    if (!pricing) return null;

    const basePrice = pricing.base_price;
    const qualityAdd = basePrice * (pricing.quality_multiplier - 1);
    const vintageAdd = basePrice * pricing.vintage_adjustment;
    const demandAdd = basePrice * (pricing.demand_factor - 1);
    const typeAdd = basePrice * (pricing.type_premium - 1);

    return (
        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-6 md:p-8 border border-gray-200 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-slate-800 rounded-xl">
                    <Target className="text-white" size={24} />
                </div>
                <div>
                    <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">Price Breakdown</h3>
                    <p className="text-xs text-gray-600 font-medium">How the price per tonne is calculated</p>
                </div>
            </div>

            {/* Price Breakdown */}
            <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                            <span className="text-xs font-black text-gray-600">BASE</span>
                        </div>
                        <div>
                            <p className="text-sm font-bold text-gray-900">Base Market Price</p>
                            <p className="text-[10px] text-gray-500">Average VCS credit price</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">AED {basePrice.toLocaleString()}</p>
                    </div>
                </div>

                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <Award size={16} className="text-emerald-600" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-gray-900">Quality Multiplier ({project.riskRating?.split(' ')[0]})</p>
                            <p className="text-[10px] text-gray-500">+{Math.round((pricing.quality_multiplier - 1) * 100)}% premium</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-lg font-bold text-emerald-600">+ AED {qualityAdd.toLocaleString()}</p>
                    </div>
                </div>

                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Clock size={16} className="text-blue-600" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-gray-900">Vintage Adjustment ({project.vintage})</p>
                            <p className="text-[10px] text-gray-500">+{Math.round(pricing.vintage_adjustment * 100)}% for recent vintage</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-lg font-bold text-blue-600">+ AED {vintageAdd.toLocaleString()}</p>
                    </div>
                </div>

                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                            <Activity size={16} className="text-orange-600" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-gray-900">Demand Factor</p>
                            <p className="text-[10px] text-gray-500">{project.progress}% sold - high demand</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-lg font-bold text-orange-600">+ AED {demandAdd.toLocaleString()}</p>
                    </div>
                </div>

                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Leaf size={16} className="text-purple-600" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-gray-900">{project.creditType} Premium</p>
                            <p className="text-[10px] text-gray-500">+{Math.round((pricing.type_premium - 1) * 100)}% for {project.creditType.toLowerCase()}</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-lg font-bold text-purple-600">+ AED {typeAdd.toLocaleString()}</p>
                    </div>
                </div>
            </div>

            {/* Final Price */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-6 text-white mb-6">
                <div className="flex justify-between items-center">
                    <div>
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Final Price per Tonne</p>
                        <p className="text-4xl font-black">AED {pricing.final_price.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Increase</p>
                        <p className="text-2xl font-black text-emerald-400">+{Math.round(((pricing.final_price - basePrice) / basePrice) * 100)}%</p>
                    </div>
                </div>
            </div>

            {/* Price History Chart */}
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <Activity size={16} className="text-gray-400" />
                    <p className="text-xs font-black text-gray-500 uppercase tracking-widest">30-Day Price History</p>
                </div>
                <div className="flex items-end gap-2 h-32">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={pricing.price_history}>
                            <Bar dataKey="price" fill="#64748B" radius={[4, 4, 0, 0]} />
                            <XAxis 
                                dataKey="date" 
                                tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                tick={{ fontSize: 10, fill: '#6B7280' }}
                                axisLine={false}
                                tickLine={false}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Info Box */}
            <div className="mt-6 p-4 bg-slate-100 rounded-xl border border-slate-200">
                <div className="flex items-start gap-3">
                    <Info size={18} className="text-slate-600 mt-0.5 shrink-0" />
                    <div>
                        <p className="text-xs font-bold text-slate-900">Why This Price?</p>
                        <p className="text-[10px] text-slate-700 mt-1 leading-relaxed">
                            This project has a {project.riskRating} quality rating from {project.riskRating?.includes('Sylvera') ? 'Sylvera' : 'BeZero'},
                            is {project.creditType.toLowerCase()} (higher permanence), and has strong demand with {project.progress}% already retired.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
